import { OnChange } from '@/features/form/types'
import { ConnectionType } from '@/features/print/constants'
import { FormKeys } from '@/features/settings/components/AddPrinterModal/form/constants'
import { schema } from '@/features/settings/components/AddPrinterModal/form/schema'
import {
    FormInput,
    FormOutput,
    OnFormSubmit,
} from '@/features/settings/components/AddPrinterModal/form/types'
import { getDefaultValues } from '@/features/settings/components/AddPrinterModal/form/utils/getDefaultValues'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    UseFormReturn,
    useController,
    useFormContext,
    useForm as useRHFForm,
    useWatch,
} from 'react-hook-form'

export const useForm = (
    preferredConnectionType: ConnectionType
): UseFormReturn<FormInput, never, FormOutput> => {
    return useRHFForm<FormInput, never, FormOutput>({
        defaultValues: getDefaultValues(preferredConnectionType),
        resolver: zodResolver<FormInput, never, FormOutput>(schema),
        mode: 'onChange',
        shouldUnregister: true,
    })
}

export const useIpAddressField = (): [string | undefined, OnChange] => {
    const {
        field: { value, onChange },
    } = useController<FormInput, FormKeys.IpAddress>({ name: FormKeys.IpAddress })

    return [value, onChange]
}

export const useConnectionTypeField = (): [ConnectionType | null, OnChange] => {
    const {
        field: { value, onChange },
    } = useController<FormInput, FormKeys.ConnectionType>({ name: FormKeys.ConnectionType })

    return [value, onChange]
}

export const useConnectionTypeValue = (): ConnectionType | null => {
    return useWatch<FormInput, FormKeys.ConnectionType>({ name: FormKeys.ConnectionType })
}

export const useFormSubmit = (
    onFormSubmit: OnFormSubmit
): [{ isValid: boolean; isSubmitting: boolean }, () => void] => {
    const {
        handleSubmit,
        formState: { isSubmitting, isValid },
    } = useFormContext<FormInput, any, FormOutput>()

    const handleValid = async (data: FormOutput) => {
        await onFormSubmit(data)
    }

    const submitHandler = handleSubmit(handleValid)

    return [{ isSubmitting, isValid }, submitHandler]
}
